import { M } from '../palette';
import { blocks, bricks, planks, solid } from './builder';
import { tracedMap } from './traced';
import { INFERNO_LAYOUT } from './traced/inferno';
import { MIRAGE_LAYOUT } from './traced/mirage';
import { SHIPMENT_LAYOUT } from './traced/shipment';

/**
 * Maps whose floor plans are traced from overview images of well-known competitive maps (tools/trace-map.py, tools/trace-plan.py;
 * see docs/maps/traced-maps.md). The plan, meaning where the lanes, rooms, cover and objective areas are and how far apart they sit,
 * is theirs; the materials, wall heights, windows and props are ours, and the names are our own.
 */

/** A two-site old town of tight alleys, a long approach and a courtyard (plan: a famous Italian-village bomb-defusal map). */
export const cobbleRow = tracedMap({
  id: 'cobble_row',
  name: 'Cobble Row',
  description: 'Narrow cobbled alleys, a long approach and two plazas to fight over.',
  layout: INFERNO_LAYOUT,
  seed: 4202,
  modes: ['tdm', 'ffa', 'dom', 'ctf', 'sandbox', 'practice'],
  style: {
    floor: [M('cobble_1'), M('cobble_2'), M('stone_2'), M('cobble_1')],
    floorEdge: M('dirt_2'),
    walls: [
      bricks([M('brick_orange'), M('brick_red_2')], 71),
      solid([M('plaster_1'), M('plaster_2')], 72),
      blocks([M('stone_1'), M('stone_3')], 3, 73),
      bricks([M('brick_yellow'), M('brick_orange')], 74),
    ],
    perimeter: blocks([M('stone_2'), M('stone_1')], 5, 75),
    trim: M('stone_3'),
    window: M('tile_black'),
    crate: planks([M('planks_2'), M('planks_1')], 76),
    heights: [48, 56, 64, 72],
    perimeterHeight: 90,
  },
  theme: {
    skyTop: 0x4f86d4,
    skyHorizon: 0xf6dcb0,
    fog: 0xf0d6aa,
    fogNear: 50,
    fogFar: 170,
    sun: [0.42, 0.62, 0.52],
    sunColor: 0xffd9a0,
    sunIntensity: 1.05,
    ambient: 0xc6d2ee,
    ambientIntensity: 0.55,
    outside: 0x9a8666,
    music: 'town',
  },
});

/** A two-site market town ringed by a long outer route and a central stair (plan: a famous desert-market bomb-defusal map). */
export const soukRun = tracedMap({
  id: 'souk_run',
  name: 'Souk Run',
  description: 'A market district with a long ring road, a central stair and two open squares.',
  layout: MIRAGE_LAYOUT,
  seed: 4303,
  modes: ['tdm', 'ffa', 'dom', 'ctf', 'sandbox', 'practice'],
  style: {
    floor: [M('sandstone_3'), M('sand_3'), M('sandstone_1'), M('sand_1')],
    floorEdge: M('dirt_3'),
    walls: [
      solid([M('plaster_3'), M('plaster_2')], 81),
      bricks([M('brick_orange'), M('brick_yellow')], 82),
      blocks([M('sandstone_2'), M('sandstone_3')], 3, 83),
      solid([M('plaster_1'), M('plaster_3')], 84),
    ],
    perimeter: blocks([M('sandstone_2'), M('sandstone_1')], 5, 85),
    trim: M('sandstone_1'),
    window: M('cloth_blue'),
    crate: planks([M('planks_3'), M('planks_2')], 86),
    heights: [44, 52, 60, 68],
    perimeterHeight: 90,
  },
  theme: {
    skyTop: 0x4f9be0,
    skyHorizon: 0xf4e2bc,
    fog: 0xf0dcb2,
    fogNear: 50,
    fogFar: 170,
    sun: [0.5, 0.78, 0.3],
    sunColor: 0xfff0c8,
    sunIntensity: 1.08,
    ambient: 0xd0dcf2,
    ambientIntensity: 0.58,
    outside: 0xd2b87c,
    music: 'desert',
  },
});

/** A tiny container yard: four stacks of containers around a clear centre (plan: a famous very small container-yard map). */
export const cargoBox = tracedMap({
  id: 'cargo_box',
  name: 'Cargo Box',
  description: 'A tiny container yard. Everything is close, and nothing stays quiet for long.',
  layout: SHIPMENT_LAYOUT,
  seed: 4404,
  modes: ['tdm', 'ffa', 'sandbox', 'practice'],
  style: {
    floor: [M('concrete_2'), M('concrete_3'), M('concrete_2'), M('concrete_1')],
    floorEdge: M('asphalt_1'),
    walls: [solid([M('paint_orange')], 91), solid([M('paint_teal')], 92), solid([M('paint_blue')], 93), solid([M('paint_red')], 94), solid([M('paint_green')], 95)],
    perimeter: blocks([M('concrete_4'), M('concrete_3')], 5, 96),
    trim: M('steel_dark_1'),
    window: M('steel_dark_2'),
    crate: planks([M('planks_2'), M('planks_3')], 97),
    heights: [26],
    perimeterHeight: 80,
  },
  theme: {
    skyTop: 0x5b8fd6,
    skyHorizon: 0xdcdcd0,
    fog: 0xd6d6cc,
    fogNear: 40,
    fogFar: 150,
    sun: [0.35, 0.8, 0.42],
    sunColor: 0xfff2d8,
    sunIntensity: 1.0,
    ambient: 0xc4d2ee,
    ambientIntensity: 0.58,
    outside: 0x808078,
    music: 'military',
  },
});
